import React, { useEffect, useState } from 'react';
import { Alert, Box, Typography, CircularProgress, Link } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { Warning as WarningIcon } from '@mui/icons-material';
import phaseService from '../../../../services/PhaseService';
import { LaunchReadiness } from '../../../../types/Phase';

interface LaunchReadinessPanelProps {
  organizationId: string;
  campaignId: string;
  onReadinessChange?: (ready: boolean) => void;
}

/**
 * Stand-alone panel that fetches launch-readiness and shows any blocking issues.
 * Notifies parent via `onReadinessChange` when readiness state changes.
 */
const LaunchReadinessPanel: React.FC<LaunchReadinessPanelProps> = ({
  organizationId,
  campaignId,
  onReadinessChange,
}) => {
  const [loading, setLoading] = useState(true);

  const [errors, setErrors] = useState<React.ReactNode[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const readiness: LaunchReadiness = await phaseService.getLaunchReadiness(
          organizationId,
          campaignId
        );
        const errs: React.ReactNode[] = [];
        if (!readiness.isReady) {
          if (readiness.missingQuestions)
            errs.push('Campaign has fewer than the minimum required questions.');
          if (readiness.invalidDates)
            errs.push(
              <span>
                One or more required campaign dates are in the past. Please review them in the{' '}
                <Link
                  component={RouterLink}
                  to={`/organizations/${organizationId}/campaigns/${campaignId}/prep/dates`}
                >
                  Dates panel
                </Link>.
              </span>
            );
          if (readiness.missingMatchingQuestions)
            errs.push(
              <span>
                Mentor/Mentee-specific questions are missing matching pairs. Please review them in the{' '}
                <Link
                  component={RouterLink}
                  to={`/organizations/${organizationId}/campaigns/${campaignId}/prep/applications`}
                >
                  Applications panel
                </Link>.
              </span>
            );
          if (readiness.invalidCampaignStatus)
            errs.push('Campaign is not in PREP status.');
        }
        setErrors(errs);
        onReadinessChange?.(errs.length === 0);
      } catch (e) {
        console.error('Error fetching readiness', e);
        setErrors(['Unable to retrieve launch readiness.']);
        onReadinessChange?.(false);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [organizationId, campaignId, onReadinessChange]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (errors.length === 0) {
    return (
      <Alert severity="success" sx={{ my: 2 }}>
        <Typography>Campaign is ready to launch.</Typography>
      </Alert>
    );
  }

  return (
    <Box sx={{ my: 2 }}>
      <Alert severity="error" icon={<WarningIcon />}>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>
          Cannot launch yet:
        </Typography>
        <ul style={{ margin: 0, paddingLeft: '1.2rem' }}>
          {errors.map((e, idx) => (
            <li key={idx}>{e}</li>
          ))}
        </ul>
      </Alert>
    </Box>
  );
};

export default LaunchReadinessPanel;
